import { Menu } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconDeviceDesktopDown,
  IconHomePlus,
  IconClipboardCheck,
  IconMessage2,
  IconPower,
  IconShieldLock,
  IconUserPlus,
  IconUserScan,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { CreatePlaceholderMemberModal } from 'src/Components/Modals/CreatePlaceholderMemberModal';
import { InviteByEmailModal } from 'src/Components/Modals/InviteByEmailModal';
import { LanguageMenuItems } from 'src/Components/NavBar/NavBarLanguageMenu';
import { useSignOut } from 'src/Hooks/Auth/useSignOut';
import useFetchApiVersion from 'src/Hooks/Queries/useFetchApiVersion';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import { useOpenInviteTypeModal } from 'src/Hooks/Shared/useOpenInviteTypeModal';
import useOverdueTaskCount from 'src/Hooks/Shared/useOverdueTaskCount';
import { usePendingInvitationsModal } from 'src/Providers/PendingInvitationsModalProvider/usePendingInvitationsModal';

import { DrowdownMenuItem } from '../DrowdownMenuItem';
import UserAvatar from '../UserAvatar';

import styles from './NavBarMenu.module.scss';
import NavBarMenuProfile from './NavBarMenuProfile';

const NavBarMenu = () => {
  const signOut = useSignOut();
  const { data: user } = useFetchUser();
  const { t } = useTranslation();
  const { open } = usePendingInvitationsModal();
  const { data: api } = useFetchApiVersion();
  const navigate = useNavigate();
  const openInviteTypeModal = useOpenInviteTypeModal();
  const { data: assetDetails } = useFetchAssetDetails();
  const isMobile = useIsMobile();
  const assetId = useCurrentAssetId();
  const { overdueCount } = useOverdueTaskCount();
  const [addMemberModalOpen, { open: openAddMemberModal, close: closeAddMemberModal }] =
    useDisclosure(false);
  const [inviteByEmailModalOpen, { open: openInviteByEmailModal, close: closeInviteByEmailModal }] =
    useDisclosure(false);

  const isOwner = assetDetails?.isOwner;

  return (
    <>
      <Menu position="bottom-end" offset={16}>
        <Menu.Target>
          <div className={styles.clickableAvatar}>
            <UserAvatar />
            <div className={styles.name}>{[user?.firstName, user?.lastName].join(' ')}</div>
          </div>
        </Menu.Target>
        <Menu.Dropdown className={styles.dropDownContainer}>
          <NavBarMenuProfile />
          <Menu.Divider />
          <DrowdownMenuItem
            label={t('navbar.menu.profile', 'Profil & Préférences')}
            icon={<IconUserScan height={22} />}
            onClick={() => navigate('/profile')}
          />
          {/* Header space is scarce on mobile, so tasks and language live here instead. */}
          {isMobile && assetId && (
            <DrowdownMenuItem
              label={
                overdueCount > 0
                  ? t('navbar.menu.tasksWithCount', 'Tâches ({{count}})', { count: overdueCount })
                  : t('tasks.badge.noTasks', 'Tâches')
              }
              icon={<IconClipboardCheck height={22} />}
              onClick={() => navigate(`/asset/${assetId}/tasks`)}
            />
          )}
          {isMobile && (
            <>
              <Menu.Divider />
              <LanguageMenuItems />
            </>
          )}
          {isOwner && assetDetails && (
            <DrowdownMenuItem
              label={t('common.inviteMember', 'Ajouter un membre')}
              icon={<IconUserPlus height={22} />}
              onClick={() =>
                openInviteTypeModal(assetDetails.id, openAddMemberModal, openInviteByEmailModal)
              }
            />
          )}
          <DrowdownMenuItem
            label={t('navbar.menu.invitations', 'Rejoindre un bien')}
            icon={<IconHomePlus height={22} />}
            onClick={() => {
              open({
                onAccept: data => {
                  navigate(`/asset/${data.asset.id}`);
                },
              });
            }}
          />
          {user?.isInternalAdmin && (
            <DrowdownMenuItem
              label={t('navbar.menu.internalBackoffice', 'Backoffice Share Heritage')}
              icon={<IconShieldLock height={22} />}
              onClick={() => navigate('/internal')}
            />
          )}
          {import.meta.env.VITE_BUG_REPORT_URL && (
            <>
              <Menu.Divider />
              <DrowdownMenuItem
                label={t('navbar.menu.feedback', 'Une idée ou un problème ?')}
                icon={<IconMessage2 height={22} />}
                onClick={() => window.open(import.meta.env.VITE_BUG_REPORT_URL, '_blank')}
              />
            </>
          )}
          <Menu.Divider />
          <DrowdownMenuItem
            label={t('navbar.menu.install', "Installer l'application")}
            icon={<IconDeviceDesktopDown height={22} />}
            onClick={() => navigate('/install')}
          />
          <Menu.Divider />
          <DrowdownMenuItem
            label={t('navbar.menu.signOut', 'Déconnexion')}
            icon={<IconPower height={22} />}
            onClick={signOut}
          />
          <Menu.Divider />
          <div className={styles.versions}>
            <div>{`app v${import.meta.env.APP_VERSION} - api v${api?.version}`}</div>
          </div>
        </Menu.Dropdown>
      </Menu>
      <CreatePlaceholderMemberModal open={addMemberModalOpen} onClose={closeAddMemberModal} />
      <InviteByEmailModal open={inviteByEmailModalOpen} onClose={closeInviteByEmailModal} />
    </>
  );
};

export default NavBarMenu;
